import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useLocation, useHistory } from 'react-router'
import { Body } from 'components/Shell/Shell'
import { TabProvider, TabPanel, TabButton } from '@tecuity/granite-ui'
import { saveNewNote, editNote, deleteNote, getEntityNotes } from '../RecordDetailsActions'
import RecordHistory from 'components/RecordHistory/RecordHistory'
import NotesPanel from '../NotesPanel'
import PartiesPanel from './PartiesPanel'
import BranchingBusinessPanel from './BranchingBusinessPanel'
import * as api from '../../../requests.js'
import { Spinner } from 'components/Loaders/Loaders'
import GenericDetails from '../GenericDetails'
import { setErrorToast } from '../../../actions.js'
import RecordDetailPageBackButton from '../RecordDetailPageBackButton.js'

const BusinessEntityRecordDetails = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const location = useLocation()
  const userRights = useSelector((state) => state.auth.appUserRights, shallowEqual)
  const [loadingRecord, setLoadingRecord] = useState(false)
  const [record, setRecord] = useState(null)
  const [recordDetailHistory, setHistory] = useState(null)
  const [recordDetailSettings, setDetailsSettings] = useState(null)
  const [activeTabId, setActiveTabId] = useState('tab-1')
  const [activeRecordDisplaySettings, setActiveRecordDisplaySettings] = useState(null)

  useEffect(() => {
    if (!userRights.includes('BusinessView')) {
      dispatch(setErrorToast('Missing BusinessView User Right.'))
      history.goBack()
      return
    }

    const path = location.pathname.split('/')
    setLoadingRecord(true)
    api.FETCH_BUSINESS_ENTITY_RECORD_DETAILS(
      path[path.length - 1],
      (record) => {
        setRecord(record)
        dispatch(getEntityNotes(record.SourceTypeId, record.Id))
        setLoadingRecord(false)
      },
      (e) => {
        console.error(e)
        setLoadingRecord(false)
      }
    )

    api.FETCH_BUSINESS_ENTITY_RECORD_DETAIL_SETTINGS(
      path[path.length - 1],
      (recordDetailSettings) => {
        setDetailsSettings(recordDetailSettings)
      },
      (e) => {
        console.error(e)
      }
    )

    api.FETCH_BUSINESS_RECORD_DETAIL_HISTORY(
      path[path.length - 1],
      (recordDetailHistory) => {
        setHistory(recordDetailHistory)
      },
      (e) => {
        console.error(e)
      }
    )
  }, [location.pathname])

  useEffect(() => {
    if (recordDetailSettings && record && recordDetailHistory) {
      const settingOptions = Object.entries(recordDetailSettings).filter(([key, value]) => +key === record.RecordType.Id)
      const selectedSetting = settingOptions.length > 0 ? settingOptions[0][1] : recordDetailSettings[0]
      setActiveRecordDisplaySettings(
        selectedSetting
      )
    }
  }, [recordDetailSettings, record, recordDetailHistory])

  if (!activeRecordDisplaySettings || loadingRecord) {
    return (
      <div style={{ height: '100%', width: '100%', textAlign: 'center', padding: '4rem' }}>
        <Spinner className="page-loader" themeColor="dark-neutral" />
      </div>
    )
  }

  const saveNote = (note) => {
    dispatch(saveNewNote(note, 54, record.Id))
  }

  const editBENote = (note) => {
    dispatch(editNote(note, 54, record.Id))
  }

  const deleteBENote = (noteId) => {
    dispatch(deleteNote(noteId, 54, record.Id))
  }

  return (
    <Body className="no-padding" /*showDrawer={showDrawer}*/>
      <TabProvider activeTabId={activeTabId} onChange={(id) => setActiveTabId(id)}>
        <div className="row header-row">
          <h1>Business Record Details</h1>
          <div className="tablist" role="tablist">
            {activeRecordDisplaySettings &&
              (activeRecordDisplaySettings.Tabs || []).map(
                (tab, i) =>
                  tab.Title !== 'General' &&
                  tab.Enabled && (
                    <TabButton
                      key={`tab-${i}`}
                      id={`tab-${i}`}
                      forPanelId={`panel-${i}`}
                      label={tab.Title}
                      customCss={
                        tab.Title === 'Notes' && record['SeeNotesFlag'] === true
                          ? { backgroundColor: 'red !important', color: '#fff !important' }
                          : {}
                      }
                    />
                  )
              )}
          </div>
        </div>

        <GenericDetails record={record} config={activeRecordDisplaySettings} />

        <div>
          {activeRecordDisplaySettings &&
            (activeRecordDisplaySettings.Tabs || []).map(
              (tab, i) =>
                tab.Title !== 'General' &&
                tab.Enabled && (
                  <TabPanel
                    key={`panel-${i}`}
                    id={`panel-${i}`}
                    tabId={`tab-${i}`}
                    label={tab.Title}
                  >
                    {tab.Title === 'Notes' ? (
                      <NotesPanel
                        saveNote={saveNote}
                        editNote={editBENote}
                        deleteNote={deleteBENote}
                      />
                    ) : tab.Title === 'Parties' ? (
                      <PartiesPanel
                        fieldSettings={activeRecordDisplaySettings.FieldSettings.Parties.Fields}
                        parties={record && record.Parties ? record.Parties : []}
                        title={tab.Title}
                      />
                    ) : tab.Title === 'Tax History' ? (
                      <PartiesPanel
                        fieldSettings={activeRecordDisplaySettings.FieldSettings.BrdTaxHistory.Fields}
                        parties={record && record.BrdTaxHistory ? record.BrdTaxHistory : []}
                        title={tab.Title}
                      />
                    ) : tab.Title === 'History' ? (
                      <React.Fragment>
                        <h2>History</h2>
                        <RecordHistory
                          history={{
                            amendments: recordDetailHistory.AmendmentList,
                            history: recordDetailHistory.HistoryList,
                            template: activeRecordDisplaySettings.HistoryConfig.filter(
                              (t) => t.enabled
                            ),
                          }}
                          requiredVoidRight="BusinessVoid"
                          requiredEditRight="EditBusinessFiling"
                          setHistory={setHistory}
                          module="business"
                        />
                      </React.Fragment>
                    ) : tab.Title === 'Alternate Business Names' ||
                      tab.Title === 'Assumed Names' ||
                      tab.Title === 'Related Entities' ? (
                      <PartiesPanel
                        fieldSettings={
                          (
                            activeRecordDisplaySettings.FieldSettings.RelatedEntities ||
                            activeRecordDisplaySettings.FieldSettings.AssumedNames
                          )?.Fields
                        }
                        parties={
                          record && (record.RelatedEntities || record.AssumedNames)
                            ? record.RelatedEntities || record.AssumedNames
                            : []
                        }
                        title={tab.Title}
                        showJVModal
                      />
                    ) : tab.Title === 'Alternative Names' ? (
                      <PartiesPanel
                        fieldSettings={
                          activeRecordDisplaySettings.FieldSettings.AlternativeNames.Fields
                        }
                        parties={record && record.AlternativeNames ? record.AlternativeNames : []}
                        title={tab.Title}
                        showJVModal
                      />
                    ) : tab.Title === 'Businesses' ? (
                      <BranchingBusinessPanel
                        fieldSettings={
                          activeRecordDisplaySettings &&
                          activeRecordDisplaySettings.FieldSettings.BranchingBusiness
                            ? activeRecordDisplaySettings.FieldSettings.BranchingBusiness.Fields
                            : {}
                        }
                        businesses={record && record.ChildBusinesses ? record.ChildBusinesses : []}
                        title={tab.Title}
                      />
                    ) : (
                      'No information for tab'
                    )}
                  </TabPanel>
                )
            )}
        </div>
      </TabProvider>
      <RecordDetailPageBackButton />
    </Body>
  )
}

export default BusinessEntityRecordDetails
